import { Toast } from 'vant';
import i18n from '@/i18n';
import { LANGUAGE } from '@/common/constants';
import { login, refresh, logout, getMemberInfo } from '@/api/member';
import { getCode, getAddressList, addAddress, updateAddress, setDefaultAddress, deleteAddress } from '@/api/common';
import { DEFAULT_REG_CHANNEL } from '@/config';

const PRIVATE_SESSION_STORAGE_KEYS = [
  'checkoutTempOrderData',
  'checkoutSavedPayMethod',
  'checkoutKycVerified',
  'checkoutKycAuthToken',
  'personalAdditionalKycAuthToken',
  'accountDeleteCompleted',
  'pageHistory',
  'isGoBack',
  'orderListScrollTop',
  'orderListRestoreScroll'
];

const state = {
  userInfo: {
    phoneNumber: '',  // 手机号
    hasWithholdingAuthorization: false,
  },
  session: {
    accessToken: '',
    refreshToken: '',
  },
  channel: DEFAULT_REG_CHANNEL,
  agreementContent: '',
  addressList: []
}

const mutations = {
  SET_SESSION(state, params = {}) {
    state.session = params;
  },
  SET_CHANNEL(state, params = '') {
    state.channel = params || DEFAULT_REG_CHANNEL;
  },
  SET_USERINFO(state, params = {}) {
    state.userInfo = params;
  },
  SET_AGREEMENT_CONTENT(state, params = '') {
    state.agreementContent = params;
  },
  SET_ADDRESS_LIST(state, params = []) {
    state.addressList = params;
  }
}

const actions = {
  getCode({ commit }, params = {}) {
    return new Promise((resolve, reject) => {
      getCode(params).then(res => {
        if (res.code === 'Success') {
          resolve(res);
        } else {
          reject(res);
        }
      }).catch(err => {
        reject(err);
      })
    })
  },
  login({ dispatch, commit }, params = {}) {
    return new Promise((resolve, reject) => {
      login(params).then(res => {
        if (res.code === 'Success' && res.data?.accessToken && res.data?.refreshToken) {
          commit('SET_USERINFO', {
            phoneNumber: params.mobile,
            hasWithholdingAuthorization: false
          })
          dispatch('setLogin', res.data);
          resolve(res);
        } else {
          reject(res);
        }
      }).catch(err => {
        reject(err);
      })
    })
  },

  refresh({ dispatch, state }) {
    return new Promise((resolve, reject) => {
      if (!state.session.refreshToken) {
        return reject('refresh_token is null');
      }
      refresh({
        refreshToken: state.session.refreshToken
      }).then(res => {
        if (res.code === 'Success' && res.data?.accessToken && res.data?.refreshToken) {
          dispatch('setLogin', res.data);
        }
        resolve(res);
      }).catch(err => {
        reject(err);
      })
    })
  },

  fetchMemberInfo({ commit, state }, config = {}) {
    return new Promise((resolve) => {
      getMemberInfo(config).then(res => {
        if (res?.code === 'Success') {
          const data = res.data || {};
          commit('SET_USERINFO', {
            phoneNumber: data.mobile || state.userInfo.phoneNumber || '',
            hasWithholdingAuthorization: [true, 'true', 1, '1'].includes(data.hasWithholdingAuthorization)
          });
        }
        resolve(res);
      }).catch(() => {
        resolve(null);
      });
    });
  },
  logout({ dispatch, state }) {
    return new Promise((resolve, reject) => {
      logout({
        refreshToken: state.session.refreshToken
      }).then(res => {
        dispatch("clearLogin")
        resolve(true)
      }).catch(err => {
        dispatch("clearLogin")
        resolve(true)
      })
    })
  },
  setLogin({ commit }, params = {}) {
    commit('SET_SESSION', params);
  },
  clearLogin({ commit }) {
    const channel = new URLSearchParams(window.location.search).get('regChannel');
    commit('SET_SESSION', {
      accessToken: '',
      refreshToken: ''
    });
    commit('SET_USERINFO', {
      phoneNumber: '',
      hasWithholdingAuthorization: false
    });
    commit('SET_AGREEMENT_CONTENT', '');
    commit('SET_ADDRESS_LIST', []);
    commit('SET_CHANNEL', channel || DEFAULT_REG_CHANNEL);
    commit('orders/CLEAR_PRIVATE_STATE', null, { root: true });
    PRIVATE_SESSION_STORAGE_KEYS.forEach(key => sessionStorage.removeItem(key));
  },
  saveAddress({}, params = {}) {
    return new Promise((resolve, reject) => {
      if (params.id) {
        updateAddress(params).then(async res => {
          if (res.code === 'Success') {
            Toast(i18n.t('saveSuccess'));
            resolve(res);
          } else {
            Toast(res.message);
            resolve(null);
          }
        }).catch(err => {
          resolve(null);
        })
      } else {
        addAddress(params).then(async res => {
          if (res.code === 'Success') {
            Toast(i18n.t('addSuccess'));
            resolve(res);
          } else {
            Toast(res.message);
            resolve(null);
          }
        }).catch(err => {
          resolve(null);
        })
      }

    })
  },
  getAddressList({ commit }, config = {}) {
    return new Promise((resolve, reject) => {
      getAddressList({}, config).then(res => {
        if (res.code === 'Success') {
          let newData = (res.data.records || []).map(item => {
            if (i18n.locale == LANGUAGE.ph) {
              item.province = item.provincePh;
              item.city = item.cityPh;
              item.region = item.regionPh;
            } else if (i18n.locale == LANGUAGE.id) {
              item.province = item.provinceId;
              item.city = item.cityId;
              item.region = item.regionId;
            } else {
              item.province = item.provinceEn;
              item.city = item.cityEn;
              item.region = item.regionEn;
            }
            return item;
          })
          commit('SET_ADDRESS_LIST', newData || []);
        }
        resolve(res)
      }).catch((err) => {
        if (config._reqLogin === false && err?.response?.status) {
          resolve({
            code: 'HttpError',
            httpStatus: err.response.status
          });
          return;
        }
        resolve(null)
      });
    })
  },
  setDefaultAddress({ commit, state }, params) {
    return new Promise((resolve, reject) => {
      setDefaultAddress(params).then(async res => {
        if (res.code === 'Success') {
          let newData = state.addressList.map(item => {
            item.isDefault = item.id == params.id ? true : false;
            return item;
          })
          commit('SET_ADDRESS_LIST', newData || []);
        }
        resolve(res);
      }).catch(err => {
        resolve(null);
      })
    })
  },
  deleteAddress({ dispatch }, params) {
    return new Promise((resolve, reject) => {
      deleteAddress(params).then(async res => {
        if (res.code === 'Success') {
          await dispatch("getAddressList");
          Toast(i18n.t('deleteSuccess'));
        } else {
          Toast(res.message);
        }
        resolve(res);
      }).catch(err => {
        resolve(null);
      })
    })
  }
}

export default {
  namespaced: true, // 启用命名空间
  state,
  mutations,
  actions
}
